import {memo, shapeComponent, ShapeComponent, Text, useBreakpoint, useI18n, useStyles} from "shared/base"
import {School, SchoolClass} from "models"
import {StyleSheet, View} from "react-native"
import ActiveStep from "routes/user/create-user/active-step"
import Button from "components/chalky-layout/button"
import Card from "components/chalky-layout/card"
import ChalkyInput from "components/chalky-layout/input"
import ChalkyLayout from "components/chalky-layout"
import {ChalkyLayoutSelectClassSteps} from "components/class-steps/select"
import {ChalkyLayoutSelectSchools} from "components/schools/select"
import {FlashNotifications as FlashMessage} from "flash-notifications"
import {Form} from "@kaspernj/api-maker/build/form"
import React from "react"
import redirectBack from "shared/redirect-back"
import setLayout from "shared/set-layout"
import StripedPressable from "components/chalky-layout/striped-pressable"
import useCollection from "@kaspernj/api-maker/build/use-collection"
import useCurrentUser from "@kaspernj/api-maker/build/use-current-user"
import useModel from "@kaspernj/api-maker/build/use-model"
import useQueryParams from "on-location-changed/build/use-query-params"
import {v4 as uuidv4} from "uuid"

const styles = StyleSheet.create({
  mb15: {
    marginBottom: 15
  },

  buttonContainer: {
    flexDirection: "row"
  },
  buttonContainerSmDown: {
    marginTop: 16,
    marginBottom: 32
  },
  buttonContainerMdUp: {
    marginTop: 48,
    justifyContent: "end"
  },

  header: {
    color: "#2c2c96",
    fontSize: 24,
    fontWeight: 700
  },

  rootContainer: {},
  rootContainerMdUp: {
    flexDirection: "row",
    width: "100%"
  },

  roleCardsContainer: {},
  roleCardsContainerXlUp: {
    flexDirection: "row",
    alignItems: "center"
  },

  rolesContainer: {
    flex: 1
  },
  rolesContainerMdUp: {
    flex: 1
  },

  continueButtonContainer: {},
  continueButtonContainerSmDown: {
    flex: 1,
    marginLeft: 10
  },

  yourRoleDescription: {
    marginBottom: 30,
    color: "#2c2c96",
    fontSize: 16
  },

  yourRoleColumn: {
    paddingTop: 37
  },
  yourRoleColumnMdUp: {
    marginTop: "auto",
    marginBottom: "auto",
    width: 300,
    paddingRight: 30
  },
  yourRoleColumnLgUp: {
    width: 500,
    paddingRight: 80
  },

  yourRoleWithDescription: {},
  yourRoleWithDescriptionMdUp: {
    marginTop: -12,
    marginLeft: 30
  },
  yourRoleWithDescriptionLgUp: {
    marginLeft: 52
  },

  yourRole: {
    marginBottom: -25,
    color: "#2c2c96",
    fontFamily: "DreamChaser",
    fontSize: 64
  },
  yourRoleSmDown: {
    textAlign: "center"
  },
  yourRoleMdUp: {
    fontFamily: "DreamChaser",
    fontSize: 64
  }
})

export default memo(shapeComponent(class RoutesUserCreateClassStep1 extends ShapeComponent {
  selectedSchool = null

  setup() {
    setLayout("user")

    const {t} = useI18n({namespace: "js.routes.user.create_class.step1"})

    this.setInstance(useBreakpoint())
    this.setInstance({
      currentUser: useCurrentUser(),
      queryParams: useQueryParams(),
      t
    })
    this.useStates({
      form: undefined,
      readMore: false
    })
    this.setSchools()
  }

  setSchools() {
    const {currentUser} = this.tt
    const {schools} = useCollection({
      modelClass: School,
      ransack: {
        s: "current_translation_name asc",
        contact_children_relationships_child_id_eq: currentUser?.contactId() || "no-user-uuid",
        contact_children_relationships_contact_relationship_type_identifier_eq_any: ["school_leader", "teacher"]
      },
      select: {
        School: ["contactId", "id", "name"]
      }
    })
    const {schoolClasses} = useCollection({
      modelClass: SchoolClass,
      ransack: {
        teacher_contacts_id_eq: currentUser?.contactId() || "no-current-user"
      }
    })
    const schoolsCacheKey = schools?.map((school) => school.id())?.join("-") || "no-schools"
    const schoolsOptions = React.useMemo(
      () => schools?.map((school) => ({school, value: school.id(), text: school.name()})),
      [schoolsCacheKey]
    )

    const {schoolClass, schoolClassLoaded} = useModel(SchoolClass, {
      match: this.p.match,
      newIfNoId: {
        defaults: () => ({id: uuidv4()})
      },
      select: {
        SchoolClass: ["classStepId", "id", "legalName", "name", "schoolId"]
      }
    })

    this.setInstance({schoolClass, schoolClassLoaded, schoolClasses, schools, schoolsOptions})
  }

  render() { // eslint-disable-line complexity
    const {mdUp, schoolClass, schoolClassLoaded, schoolClasses, schools, schoolsOptions, t, xlUp} = this.tt
    const {readMore} = this.s
    const buttonContainerStyle = useStyles(styles, "buttonContainer")
    const continueButtonContainerStyle = useStyles(styles, "continueButtonContainer")
    const rootContainerStyle = useStyles(styles, "rootContainer")
    const yourRoleColumnStyle = useStyles(styles, "yourRoleColumn")
    const rolesContainerStyle = useStyles(styles, "rolesContainer")
    const yourRoleStyle = useStyles(styles, "yourRole")
    const yourFocusDescriptionStyle = useStyles(styles, "yourRoleDescription")
    const yourRoleWithDescriptionStyle = useStyles(styles, "yourRoleWithDescription")

    return (
      <ChalkyLayout
        dataSet={this.layoutDataSet ||= {route: "user/create-class/step1"}}
        maxWidth={1260}
        schoolClass={schoolClass}
        userMenu={false}
        weekBar={false}
      >
        {schoolClasses?.length === 0 && schools?.length === 0 && !schoolClass && schoolClassLoaded &&
          <Text>You dont have access to any schools or classes</Text>
        }
        {schoolClass && (schoolClasses?.length >= 1 || schools?.length >= 1) &&
          <View dataSet={this.rootContainerDataSet ||= {class: "root-container"}} style={rootContainerStyle}>
            <View dataSet={this.yourRoleColumnDataSet ||= {class: "your-role-column"}} style={yourRoleColumnStyle}>
              <ActiveStep active label={t(".step_1")} />
              <View style={yourRoleWithDescriptionStyle}>
                <Text style={yourRoleStyle}>
                  {t(".class_details")}
                </Text>
                {!mdUp &&
                  <View style={styles.mb15}>
                    <StripedPressable onPress={this.tt.onReadMorePress}>
                      <Text>
                        {readMore ? t(".read_less") : t(".read_more")}
                      </Text>
                    </StripedPressable>
                  </View>
                }
                {(mdUp || readMore) &&
                  <Text style={yourFocusDescriptionStyle}>
                    {t(".class_details_description")}
                  </Text>
                }
              </View>
            </View>
            <View dataSet={this.rolesContainerDataSet ||= {class: "roles-container"}} style={rolesContainerStyle}>
              <Card>
                <Form onSubmit={this.tt.onSubmit} setForm={this.setStates.form}>
                  <View style={this.formContainerStyle ||= {flexDirection: "row", flexWrap: "wrap"}}>
                    {schoolsOptions !== undefined && schoolsOptions.length > 0 &&
                      <View style={{width: "100%", marginBottom: 20}}>
                        <ChalkyLayoutSelectSchools
                          defaultValues={schoolClass.hasSchoolId() ? [schoolClass.schoolId()] : null}
                          id="school_class_school_id"
                          label={School.modelName().human()}
                          name="school_class[school_id]"
                          options={schoolsOptions}
                          placeholder={t(".please_select_school")}
                        />
                      </View>
                    }
                    <View
                      style={this.classStepContainerStyle ||= {
                        width: "100%",
                        marginBottom: 20
                      }}
                    >
                      <ChalkyLayoutSelectClassSteps
                        defaultValues={schoolClass.hasClassStepId() ? [schoolClass.classStepId()] : null}
                        id="class_step_id"
                        label={t(".grade_level")}
                        name="school_class[class_step_id]"
                        placeholder={t(".eleventh")}
                      />
                    </View>
                    <View
                      style={this.cache("schoolClassLegalNameContainerStyle", {
                        width: xlUp ? "50%" : "100%",
                        paddingRight: xlUp ? 8 : undefined
                      }, [xlUp])}
                    >
                      <ChalkyInput
                        dataSet={this.schoolClassLegalNameInputDataSet ||= {class: "school-class-legal-name-input"}}
                        defaultValue={schoolClass.legalName()}
                        label={t(".official_class_name_or_id")}
                        name="school_class[legal_name]"
                        placeholder={t(".school_class_legal_name_placeholder")}
                        testID="school-class-legal-name-input"
                      />
                    </View>
                    <View
                      style={this.cache("schoolClassNameContainerStyle", {
                        width: xlUp ? "50%" : "100%",
                        paddingLeft: xlUp ? 8 : undefined
                      }, [xlUp])}
                    >
                      <ChalkyInput
                        dataSet={this.schoolClassNameInputDataSet ||= {class: "school-class-name-input"}}
                        defaultValue={schoolClass.name()}
                        label={t(".class_nickname")}
                        name="school_class[name]"
                        placeholder={t(".school_class_nickname_placeholder")}
                        testID="school-class-name-input"
                      />
                    </View>
                  </View>
                </Form>
              </Card>
              <View style={buttonContainerStyle}>
                <View style={continueButtonContainerStyle}>
                  <Button
                    onPress={this.tt.onCreateClassPress}
                    primary
                    testID="save-class-button"
                    title={schoolClass.isNewRecord() ? t(".create_class") : t(".save_class")}
                  />
                </View>
              </View>
            </View>
          </View>
        }
      </ChalkyLayout>
    )
  }

  onSubmit = () => this.tt.onCreateClassPress()

  onCreateClassPress = async () => {
    const {queryParams, schoolClass, schoolClasses, schools, t} = this.tt
    const formData = this.s.form.asObject()

    if (schoolClass.isNewRecord() && !formData.school_class.school_id) {
      if (schools.length == 1) {
        formData.school_class.school_id = schools[0].id()
      } else if (schoolClasses.length >= 1) {
        formData.school_class.school_id = schoolClasses[0].schoolId()
      } else {
        throw new Error("Couldn't figure out how to get a school for the new class")
      }
    }

    try {
      if (schoolClass.isNewRecord()) {
        await SchoolClass.createNew(formData)
        FlashMessage.success(t(".your_class_have_been_created"))
      } else {
        await schoolClass.saveRaw(formData)
        FlashMessage.success(t(".your_class_was_updated"))
      }

      redirectBack({defaultPath: "userRoot", queryParams})
    } catch (error) {
      FlashMessage.errorResponse(error)
    }
  }

  onReadMorePress = () => this.setState({readMore: !this.s.readMore})
}))
